/**
 * THE UNIFIED APPROVAL QUEUE.
 *
 * Everything approvable in the product raises a workflow instance, so this one inbox
 * replaces what would otherwise be five or six separate queues (timesheets, leave,
 * expenses, POs, payroll, comp changes). One queue means a manager has one place to look
 * and nothing can hide in a tab they never open.
 *
 * Keyboard: `j`/`k` or arrows move, `a` approves, `r` rejects, `x` toggles selection.
 * A manager clearing thirty timesheets on a Monday morning should never need the mouse.
 *
 * Bulk approve exists but bulk REJECT deliberately does not: a rejection needs a reason
 * that is specific to the request, and a shared reason applied to twenty items is worse
 * than no reason at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCheck, CheckCircle2, Inbox, XOctagon } from 'lucide-react';

import {
  useDecideStep,
  usePendingApprovals,
  type PendingApproval,
} from '@frontend/api/hooks/workflows';
import { cn } from '@frontend/lib/cn';
import { formatDateTime, formatRelative, humanise } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { Textarea } from '@frontend/components/ui/Field';
import { Modal } from '@frontend/components/ui/Modal';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

/** Groups the process key into a filter bucket, e.g. `leave.request.submit` → `leave`. */
function moduleOf(processKey: string): string {
  return processKey.split('.')[0] ?? 'other';
}

/** Where the underlying record lives, so "open" goes somewhere useful. */
function recordHref(item: PendingApproval): string | null {
  switch (item.entityType) {
    case 'timesheet':
      return `/timesheets/${item.entityId}`;
    case 'leave_request':
      return `/leave/${item.entityId}`;
    case 'expense':
    case 'expense_report':
      return `/expenses/${item.entityId}`;
    case 'purchase_order':
      return `/purchase-orders/${item.entityId}`;
    case 'payroll_run':
      return `/payroll/${item.entityId}`;
    case 'project':
      return `/projects/${item.entityId}`;
    default:
      return null;
  }
}

export function ApprovalInbox(): JSX.Element {
  const query = usePendingApprovals();
  const decide = useDecideStep();
  const toast = useToast();

  const [filter, setFilter] = useState<string>('ALL');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [rejecting, setRejecting] = useState<PendingApproval | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const modules = useMemo(
    () => [...new Set(items.map((item) => moduleOf(item.processKey)))].sort(),
    [items],
  );
  const visible = useMemo(
    () => (filter === 'ALL' ? items : items.filter((item) => moduleOf(item.processKey) === filter)),
    [items, filter],
  );

  const approve = useCallback(
    (item: PendingApproval, comment?: string) => {
      decide.mutate(
        {
          instanceId: item.instanceId,
          stepInstanceId: item.stepInstanceId,
          decision: 'APPROVED',
          comment: comment ?? null,
        },
        {
          onSuccess: () => toast.success('Approved', item.summary),
          onError: (error) => toast.fromError(error, 'The approval was not recorded'),
        },
      );
    },
    [decide, toast],
  );

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      if (target !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (visible.length === 0) return;

      const current = visible[Math.min(cursor, visible.length - 1)];

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setCursor((index) => Math.min(index + 1, visible.length - 1));
      } else if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setCursor((index) => Math.max(index - 1, 0));
      } else if (event.key === 'a' && current !== undefined) {
        event.preventDefault();
        approve(current);
      } else if (event.key === 'r' && current !== undefined) {
        event.preventDefault();
        setRejecting(current);
      } else if (event.key === 'x' && current !== undefined) {
        event.preventDefault();
        setSelected((previous) => {
          const next = new Set(previous);
          if (next.has(current.stepInstanceId)) next.delete(current.stepInstanceId);
          else next.add(current.stepInstanceId);
          return next;
        });
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, cursor, approve]);

  useEffect(() => {
    listRef.current?.querySelectorAll('li')[cursor]?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (query.isLoading) return <LoadingRows rows={6} columns={5} />;
  if (query.isError) {
    return (
      <>
        <PageHeader title="My approvals" />
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </>
    );
  }

  const overdueCount = items.filter((item) => item.isOverdue).length;

  return (
    <div>
      <PageHeader
        title="My approvals"
        description="Everything waiting on a decision from you, across every module. Keyboard: j / k to move, a to approve, r to reject, x to select."
        actions={
          selected.size > 0 ? (
            <Button
              variant="primary"
              leadingIcon={<CheckCheck className="h-3.5 w-3.5" />}
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-approve"
            >
              Approve {selected.size} selected
            </Button>
          ) : undefined
        }
      />

      {overdueCount > 0 && (
        <Banner tone="warning" className="mb-3" title={`${overdueCount} past their service level`}>
          These have been waiting longer than the workflow allows. The requester is blocked until you decide.
        </Banner>
      )}

      {items.length === 0 ? (
        <div className="surface-card">
          <EmptyState
            title="Nothing waiting on you"
            description="When a timesheet, leave request, expense or purchase order needs your decision it appears here — and you get a notification. There is nothing to do right now."
            icon={<Inbox aria-hidden className="h-7 w-7" />}
          />
        </div>
      ) : (
        <>
          {modules.length > 1 && (
            <div className="mb-3 flex flex-wrap gap-1" role="tablist" aria-label="Filter by module">
              {['ALL', ...modules].map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={filter === key}
                  onClick={() => {
                    setFilter(key);
                    setCursor(0);
                  }}
                  data-testid={`filter-${key.toLowerCase()}`}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-body',
                    filter === key
                      ? 'border-interactive bg-interactive-subtle font-medium text-content'
                      : 'border-line text-content-secondary hover:text-content',
                  )}
                >
                  {key === 'ALL' ? 'All' : humanise(key)}
                  <span className="ml-1 tabular text-content-tertiary">
                    {key === 'ALL' ? items.length : items.filter((item) => moduleOf(item.processKey) === key).length}
                  </span>
                </button>
              ))}
            </div>
          )}

          <ul ref={listRef} className="space-y-2" data-testid="approval-list">
            {visible.map((item, index) => {
              const href = recordHref(item);
              const isCursor = index === cursor;
              return (
                <li
                  key={item.stepInstanceId}
                  data-testid={`workflow-item-${item.entityId}`}
                  className={cn(
                    'surface-card flex flex-wrap items-start gap-3 p-3 transition-token',
                    isCursor && 'ring-2 ring-line-focus',
                    item.isOverdue && 'border-l-4 border-l-error',
                  )}
                  onClick={() => setCursor(index)}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select ${item.summary}`}
                    checked={selected.has(item.stepInstanceId)}
                    onChange={() =>
                      setSelected((previous) => {
                        const next = new Set(previous);
                        if (next.has(item.stepInstanceId)) next.delete(item.stepInstanceId);
                        else next.add(item.stepInstanceId);
                        return next;
                      })
                    }
                    className="mt-1 h-3.5 w-3.5 shrink-0 cursor-pointer accent-interactive"
                    data-testid={`select-${item.stepInstanceId}`}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-sm bg-surface-muted px-1.5 py-0.5 text-body font-semibold uppercase tracking-[0.04em] text-content-secondary">
                        {humanise(moduleOf(item.processKey))}
                      </span>
                      <StatusBadge status={item.isOverdue ? 'OVERDUE' : 'PENDING'} label={item.stepName} />
                    </div>
                    <p className="mt-1 text-row font-medium text-content">{item.summary}</p>
                    <p className="mt-0.5 text-body text-content-tertiary">
                      Raised {formatRelative(item.requestedAt)}
                      {item.dueAt !== null && (
                        <>
                          {' · '}
                          {item.isOverdue ? 'was due ' : 'due '}
                          {formatDateTime(item.dueAt)}
                        </>
                      )}
                      {item.minApprovals > 1 && (
                        <>
                          {' · '}
                          {item.approvalCount} of {item.minApprovals} approvals
                        </>
                      )}
                    </p>
                    {href !== null && (
                      <Link to={href} className="mt-1 inline-block text-body text-content-link">
                        Open the record
                      </Link>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="dangerGhost"
                      leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
                      onClick={() => setRejecting(item)}
                      data-testid={`reject-${item.entityId}`}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      leadingIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                      onClick={() => approve(item)}
                      data-testid={`approve-${item.entityId}`}
                    >
                      Approve
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <RejectDialog
        item={rejecting}
        onClose={() => setRejecting(null)}
        onReject={(item, reason) => {
          decide.mutate(
            {
              instanceId: item.instanceId,
              stepInstanceId: item.stepInstanceId,
              decision: 'REJECTED',
              comment: reason,
            },
            {
              onSuccess: () => toast.success('Rejected', 'The requester has been told why.'),
              onError: (error) => toast.fromError(error, 'The rejection was not recorded'),
            },
          );
          setRejecting(null);
        }}
      />

      <BulkApproveDialog
        open={bulkOpen}
        count={selected.size}
        onClose={() => setBulkOpen(false)}
        onConfirm={(comment) => {
          const chosen = items.filter((item) => selected.has(item.stepInstanceId));
          for (const item of chosen) approve(item, comment);
          setSelected(new Set());
          setBulkOpen(false);
          toast.success(`${chosen.length} approved`, 'Each one is audited on its own line.');
        }}
      />
    </div>
  );
}

function RejectDialog({
  item,
  onClose,
  onReject,
}: {
  item: PendingApproval | null;
  onClose: () => void;
  onReject: (item: PendingApproval, reason: string) => void;
}): JSX.Element | null {
  const [reason, setReason] = useState('');
  useEffect(() => {
    setReason('');
  }, [item]);

  if (item === null) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Reject this request"
      description={item.summary}
      size="sm"
      dismissible={reason === ''}
      data-testid="reject-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="dangerGhost"
            leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
            disabled={reason.trim().length < 3}
            onClick={() => onReject(item, reason.trim())}
            data-testid="confirm-reject"
          >
            Reject
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="mb-1 block text-body font-medium text-content-secondary">
          Why are you rejecting it? <span className="text-error">*</span>
        </span>
        <Textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          autoFocus
          rows={4}
          placeholder="Be specific — this is what the requester sees, and it is the only guidance they get on what to change."
          data-testid="input-reject-reason"
        />
      </label>
      {reason.trim().length > 0 && reason.trim().length < 3 && (
        <p className="mt-1 text-body text-error" role="alert">
          Give a reason of at least three characters. "No" without an explanation just produces a resubmission
          of the same request.
        </p>
      )}
    </Modal>
  );
}

function BulkApproveDialog({
  open,
  count,
  onClose,
  onConfirm,
}: {
  open: boolean;
  count: number;
  onClose: () => void;
  onConfirm: (comment: string | undefined) => void;
}): JSX.Element {
  const [comment, setComment] = useState('');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Approve ${count} request${count === 1 ? '' : 's'}?`}
      size="sm"
      data-testid="bulk-approve-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
            onClick={() => {
              onConfirm(comment.trim() === '' ? undefined : comment.trim());
              setComment('');
            }}
            data-testid="confirm-bulk-approve"
          >
            Approve all {count}
          </Button>
        </>
      }
    >
      <p className="text-row text-content">
        Each request is approved and audited individually. If one fails the others still go through, and the
        failures come back to this queue.
      </p>
      <label className="mt-3 block">
        <span className="mb-1 block text-body font-medium text-content-secondary">Comment (optional)</span>
        <Textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} />
      </label>
    </Modal>
  );
}
